import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { memo, useCallback, useState } from 'react';
import type { SectionCoverage } from '../collapsed-table/types';
import { PlanCellValue } from '../plan-cell-value';
import { getColumnKey } from '../pricing-cards/catalog';
import * as SX from './sx';
import type { CollapsibleSectionProps as Props } from './types';

// `countUnit` arrives plural ("filters", "features"); singularize when there's
// exactly one so the counter reads "1 of 1 feature".
function countNoun(unit: string | undefined, total: number): string {
  const noun = unit || 'features';
  return total === 1 ? noun.replace(/s$/, '') : noun;
}

function SectionHeaderPreview({ coverage }: { coverage?: SectionCoverage }) {
  if (!coverage || coverage.total === 0) {
    return <PlanCellValue value="dash" />;
  }
  const label = coverage.filled === coverage.total ? 'Full Access' : 'Limited';
  return (
    <Stack sx={SX.coverageStack}>
      <Typography variant="body2" color="grey.700" noWrap>
        {label}
      </Typography>
      <Typography variant="body5" color="grey.500" noWrap>
        {coverage.filled} of {coverage.total}{' '}
        {countNoun(coverage.unit, coverage.total)}
      </Typography>
    </Stack>
  );
}

export const CollapsibleSection = memo(
  ({ section, products, isLast }: Props) => {
    const [open, setOpen] = useState(false);

    const handleToggle = useCallback(() => {
      setOpen((prev) => !prev);
    }, []);

    // When this is the last section and it's collapsed, its header row is the
    // table's bottom edge, so its cells drop their bottom border.
    const atBottom = !!isLast && !open;

    return (
      <>
        <TableRow sx={SX.sectionHeaderRow} onClick={handleToggle}>
          <TableCell sx={SX.sectionHeaderCell({ open, atBottom })}>
            <IconButton size="small" sx={SX.collapseButton}>
              {open ? (
                <KeyboardArrowUpIcon fontSize="small" />
              ) : (
                <KeyboardArrowDownIcon fontSize="small" />
              )}
            </IconButton>
            <Typography variant="body2" fontWeight={500}>
              {section.title}
            </Typography>
          </TableCell>
          {products.map((product) => (
            <TableCell
              key={product.id}
              align="center"
              sx={SX.sectionHeaderValueCell({ open, atBottom })}
            >
              <SectionHeaderPreview
                coverage={section.headerCoverage[getColumnKey(product)]}
              />
            </TableCell>
          ))}
        </TableRow>
        <TableRow>
          <TableCell
            sx={SX.collapsibleWrapperCell}
            colSpan={products.length + 1}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Table
                size="small"
                sx={isLast ? SX.collapseInnerLast : undefined}
              >
                <TableBody>
                  {section.features.map((row) => (
                    <TableRow key={row.id} sx={SX.featureRow}>
                      <TableCell sx={SX.featureNameCell}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          color="text.secondary"
                          noWrap
                        >
                          {row.feature}
                        </Typography>
                        {row.badge && (
                          <Typography variant="body5" color="grey.500">
                            {row.badge}
                          </Typography>
                        )}
                      </TableCell>
                      {products.map((product) => {
                        const columnKey = getColumnKey(product);
                        return (
                          <TableCell
                            key={product.id}
                            align="center"
                            sx={SX.featureValueCell}
                          >
                            <PlanCellValue
                              value={row.values[columnKey] ?? 'dash'}
                              subtext={row.subtexts?.[columnKey]}
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }
);

CollapsibleSection.displayName = 'CollapsibleSection';
